import {
  Box,
  Card,
  CardProps,
  Typography,
  TypographyProps,
  useTheme,
} from "@mui/material";
import { FC } from "react";

import { ProgressBar } from "../../molecules/progress-bar/progress-bar";

export interface ArtefactsProgressCardV2Props {
  /** card title */
  cardTitle: string;
  /** subtitle */
  subtitle?: string;
  /** card Icon */
  cardIcon?: string;
  /** card props */
  cardProps?: CardProps;
  /** typography props */
  titleProps?: TypographyProps;
  /** if set to true, the content in the card will be center-aligned */
  center?: boolean;
  centerTitle?: boolean;
  progressBarProps: {
    /** bar progress */
    value: number;
    /** bar background color */
    backgroundColor?: string;
    /** (optional) label to display for the bar */
    label?: string;
    /** value to display as number next to label */
    displayValue?: string;
    /** (optional) bar color  */
    color?: string;
  };
}

export const ArtefactsProgressCardV2: FC<ArtefactsProgressCardV2Props> = ({
  cardTitle,
  cardIcon,
  progressBarProps,
  subtitle,
  center,
  centerTitle = true,
  cardProps = {},
  titleProps = {},
}) => {
  const theme = useTheme();
  const {
    backgroundColor = "#f5f5f5",
    label,
    value,
    displayValue,
    color = theme.palette.secondary.main,
  } = progressBarProps;
  return (
    <Card
      sx={{
        backgroundColor: theme.palette.primary.light,
        boxShadow: "none",
        borderRadius: "12px",
        display: "flex",
        flexDirection: "column",
        alignItems: center ? "center" : "flex-start",
        height: "100%",
        p: 2,
        pb: 3,
        "@media print": {
          border: "1px solid rgba(0, 0, 0, 0.12)",
        },
        ...cardProps.sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          width: "100%",
          alignItems: "center",
          mb: 2,
        }}
      >
        {cardIcon && (
          <Box
            component="img"
            sx={{
              background: "white",
              borderRadius: "12px",
              width: "36px",
              height: "36px",
              padding: "5px",
              mr: 2,
            }}
            src={cardIcon}
          />
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: centerTitle ? "center" : "flex-start",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: theme.palette.primary.contrastText,
              ...titleProps.sx,
            }}
          >
            {cardTitle}
          </Typography>
          {subtitle && (
            <Typography
              variant="subtitle2"
              sx={{
                color: theme.palette.primary.contrastText,
                ...titleProps.sx,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ flex: "1 1 100%" }} />
      <Box
        data-testid="progress-bar-title-value"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          {label && (
            <Typography
              variant="subtitle2"
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                color: theme.palette.primary.contrastText,
                m: 0,
              }}
            >
              {label}
            </Typography>
          )}
          <Typography
            variant="subtitle2"
            sx={{
              whiteSpace: "nowrap",
              color: theme.palette.primary.contrastText,
              textAlign: "right",
            }}
          >
            {displayValue}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              flex: 1,
              mr: "10px",
            }}
          >
            <ProgressBar
              progress={value}
              progressColor={color}
              backgroundColor={backgroundColor}
              progressBarHeight="12px"
            />
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default ArtefactsProgressCardV2;
